import React, { useEffect, useState, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { getRemotedbUrl, i18nIntl, isDesktop, isNotEmpty } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';
import { stage_promotion_types as spt } from '../../data/bootstrap_stages.js';
import md5 from 'md5';

import ActionButton from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import CloseButton from '../builder/CloseButton';
import ImageUploader, { uploadImage } from '../builder/ImageUploader';
import Range from '../builder/Range';
import SaveButton from '../builder/SaveButton.js';

import FloatingLayerPublic from './FloatingLayerPublic';

import LeveledHTMLText from './helpers/LeveledHTMLText.js';

import SimpleFabric from '../canvas/SimpleFabric';
import PhotoFunCanvas from '../canvas/PhotoFunCanvas.js';
import EarnedPointsMessage from './loyalty/EarnedPointsMessage.js';
import NextActionButton from '../games/NextActionButton.js';
import PromoColsWrapper from './PromoColsWrapper.js';
import { AlertMessage } from '../builder/Message.js';
import ShareButton from './buttons/ShareButton.js';

const SingleItem = (props) => {
	const type = props?.type ?? 'sticker';
	const name = props?.name ?? false;
	const image = props?.image ?? false;
	const meta = props?.meta ?? false;
	const is_empty = props?.is_empty ?? false;
	const img_proportion = props?.img_proportion ?? '1x1';

	if(image === false) return null;

	const item_styles = {
		backgroundImage:`url(${image})`,
		backgroundSize:(is_empty) ? '30px' : null,
	}

	const getItemClasses = () => {
		let is_selected = false;
		if(is_empty && meta == '') is_selected = true;
		if(!is_empty && meta != '' && image.indexOf(meta) != -1) is_selected = true;
		if(type != 'overlay') is_selected = false;

		const item_classes = ['photo_editor_thumb', 'gui_with_alpha_bg'];
		if(!is_selected) item_classes.push('original_color_item');

		return item_classes.join(" ");
	};

	return (
		<>
			<div class={getItemClasses()}>
				<div style={item_styles} onClick={props.onClick}>
					<img src={`${window.cdn_url}img/blank_${img_proportion}.png`} width="100%" />
				</div>
			</div>

			{(name !== false) && (
				<div class="item_name">{name}</div>
			)}
		</>
	)
}

const ItemSelector = ({ items, stage, promo, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const type = props?.type ?? 'overlay';
	
	const meta = props?.meta ?? {};
	
	const display_names = stage?.photo_editor_sticker_name ?? false;
	const img_proportion = stage?.photo_editor_proportion ?? '1x1';	// w:h

	let mandatory_frame = stage?.photo_editor_mandatory_frame ?? false;
	if(stage.promotion_type_id == spt.SCENES.id) mandatory_frame = true;

	const is_light_layout = ((promo?.config?.layout?.text_color ?? 'wt') == 'wt') ? true : false;

	const classes = ['gui_img_selector', ((type == 'sticker') ? 'stickers' : 'frames')];
	if(display_names) classes.push('with_names');

	const addItem = (item) => {
		if(props.addItem == undefined) return;

		props.addItem(type, {
			url:item.full,
			name:item.name,
			image:item.image,
		});
	};

	const removeItem = () => {
		if(props.removeItem == undefined) return;

		props.removeItem();
	};

	return (
		<ul class={classes.join(" ")} style={{width:'100%'}}>
			{(type == 'overlay' && !mandatory_frame) && (
				<li>
					<SingleItem
						type={type}
						image={`/img/photo_editor_svg/delete_${(is_light_layout) ? 'bk' : 'wt'}.svg`}
						name={(display_names) ? i18n("Empty") : null}
						onClick={removeItem}
						meta={meta}
						is_empty={true}
						img_proportion={img_proportion}
					/>
				</li>
			)}

			{items.map((item, i) => {
				return (
					<li key={i}>
						<SingleItem
							type={type}
							image={item.full}
							name={(display_names) ? item.name : null}
							onClick={() => addItem(item)}
							meta={meta}
							is_empty={false}
							img_proportion={img_proportion}
						/>
					</li>
				)
			})}
		</ul>
	)
};

const StickerSelector = ({ stage, ...props }) => {
	const type = props?.type ?? 'sticker';
	const is_mobile = props?.is_mobile ?? false;
	const validation = props?.validation ?? {};

	const min_items = stage?.photo_editor_num_stickers_min ?? 0;
	const max_items = stage?.photo_editor_num_stickers_max ?? false;
	const display_names = stage?.photo_editor_sticker_name ?? false;

	const classes = ['gui_img_selector', ((type == 'sticker') ? 'stickers' : 'frames')];
	if(display_names) classes.push('with_names');

	const list_classes = ['stickers_list', 'gui_overflowed_auto'];
	if(!is_mobile && min_items > 0 && validation !== true) list_classes.push('with_message');
	if(validation == 'max') list_classes.push('gui_withoutEvents');

	return (
		<div class="gui_pos_rel" style={{ height:'100%', width:'100%' }}>
			{(!is_mobile && min_items > 0 && validation !== true) && (
				<span id="warning_min_stickers" class={((validation != 'min') ? 'greenBg' : 'redBg')}>
					{(min_items != max_items) ? (
						<FormattedMessage
							id="You have to select, at least, {min_stickers} stickers"
							values={{ min_stickers:min_items }}
						/>
					) : (
						<FormattedMessage
							id="You have to select {stickers} stickers"
							values={{ stickers:min_items }}
						/>
					)}
				</span>
			)}

			<ItemSelector
				stage={stage}
				{...props}
			/>

			{(validation == 'max') && (
				<div id="max_stickers_error_msg" class="not_in_top">
					<FormattedMessage
						id="The maximum stickers is {max}"
						values={{ max:max_items }}
					/>
				</div>
			)}
		</div>
	)
};

const ShareThis = ({ stage, promotion, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_builder = props?.is_builder ?? false;

	if(is_builder) {
		return (
			<div class="share_btn gui_big_button flex_button">
				<ActionButton
					_fullclass=""
					onClick={() => { alert("This will open the Share dialog")}}
				>{stage?.button_label ?? i18n("Share")}</ActionButton>
			</div>
		)
	}

	const locale = (props.locale == undefined) ? '' : '?lc='+props.locale;
	const result = props?.result ?? false;

	const participation_hash = md5(`${result.uid}EPTEAM`).slice(0, 6);
	const participation_id = result.uid ?? 'test';
	const share_url_base = (!is_builder) ? `https://${window.domain_url}` : window.public_url;

	const share_title = stage?.share_title ?? i18n("This is my photo");
	const share_description = stage?.share_description ?? i18n("You can create your own!");
	const share_tweet = stage?.share_tweet ?? (promotion?.post_tweet ?? share_title);
	const share_instant = stage?.share_instant ?? (promotion?.post_instant ?? share_title);

	const image = result?.team_image ?? false;

	const share_layer_props = {
		is_builder,
		promotion,
		info:{
			url:`${share_url_base}/share/${participation_id}/${participation_hash}${locale}`,
			image:image,
			title:share_title,
			descriptionenc:share_description,
			postenc:encodeURI(share_tweet),
			instantenc:encodeURI(share_instant),
			urlencodeparameter:'',
		},
		is_mobile:!isDesktop(),
		enable_behavior:(is_builder) ? true : null,
		layer_title:i18n(props?.layer_title ?? "Share your photo"),
		sharing_type:'photofun',
		sharing_page:'photofun',
	};

	return (
		<ShareButton
			{...share_layer_props}
			icon={false}
			{...props.custom_cta_props}
		>{stage?.button_label ?? i18n("Share")}</ShareButton>
	)
};

export const PhotoFunPublicShare = ({ stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const user = props?.user ?? false;
	const promotion = props?.promotion ?? false;
	const is_builder = props?.is_builder ?? false;

	const img_proportion = stage?.photo_editor_proportion ?? '1x1';	// w:h

	const main_shared_placeholder = `<h1>${i18n("The design of {name}")}</h1><p>${i18n("Want to take part in the contest? Design your own image now in less than 2 minutes.")}</p>`;

	let user_name = i18n("Player");
	if(!is_builder) {
		user_name = (promotion.need_nickname != undefined && promotion.need_nickname == '1') ? user.name : user.first_name;
	}

	let main_description = stage?.shared_description ?? "";
	if(main_description == "") main_description = main_shared_placeholder;

	let button_cta = stage?.shared_button_label ?? "";
	if(button_cta == "") button_cta = i18n("Create my own");
	let image = user?.quiz_entry?.media[0] != undefined ? `${user.quiz_entry.media[0].full_path}normal/${user.quiz_entry.media[0].filename}`:false;
	if(is_builder) {
		if(image === false) image = `${window.cdn_url}img/blank_${img_proportion}.png`;
	}

	let image_classes = ['photofun_image'];
	if(is_builder) image_classes.push('gui_remove_pointer_events demo_image');

	return (
		<div class="photofun_shared">
			<article class="photofun_description gui_main_text_color_on_bg">
				<LeveledHTMLText
					_class={{ other:'fill_description' }}
				>{main_description.replace("{name}", user_name)}</LeveledHTMLText>
			</article>

			{(image !== false) && (
				<div class={image_classes.join(" ")}>
					<ActionLink _href={image} _target='_blank'>
						{(!is_builder) ? (
							<img src={`${image}`} width="100%" />
						) : (
							<span style={{
								backgroundColor:'rgba(var(--text-color-rgb), 0.05)',
								display:'inline-block',
								width:'300px',
							}}>
								<img src={image} width="100%" />
							</span>
						)}
					</ActionLink>
				</div>
			)}

			<article class="photofun_cta template_module info_flexwrap gui_main_actions">
				<div class="template_submodule call_to_action_flex enter_flexwrap">
					<div class="enter flex_button gui_big_button register_flex">
						<ActionButton
							_fullclass=""
							_href={`/p/${promotion.id}`}
							autotranslate={false}
						>{button_cta}</ActionButton>
					</div>
				</div>
			</article>
		</div>
	)
};

const PhotoFunStage = (props) => {
	useCSS('comp/photoeditor.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promo = props?.promotion.Promotion ?? false;
	const stage = props?.stage?.Stage ?? (props?.stage ?? false);
	const is_builder = props?.builder ?? false;
	const is_mobile = props?.is_mobile ?? false;

	const stickers = stage?.photo_editor_images?.stickers ?? [];
	const marks = stage?.photo_editor_images?.marks ?? [];
	const has_stickers = (stickers.length > 0) ? true : false;
	const has_frames = (marks.length > 0) ? true : false;

	const cnv = useRef(null);

	const default_meta = {
		marks:'',
		stickers:{},
	};

	const full_photo_editor_custom = (isNotEmpty(stage, 'full_photo_editor_custom')) ? stage.full_photo_editor_custom : false;

	const [ display, setDisplay ] = useState((stage.promotion_type_id == spt.SCENES.id) ? ((marks.length > 0) ? 'frames' : 'stickers') : ((full_photo_editor_custom === false) ? 'photo' : 'stickers'));
	const [ display_confirm_delete, setDisplayConfirmDelete ] = useState(false);
	const [ display_editor, setDisplayEditor ] = useState(false);
	const [ exported_image, setExportedImage ] = useState(props?.exported_image ?? false);
	const [ display_mobile_options, setDisplayMobileOptions ] = useState(false);
	const [ meta, setMeta ] = useState(default_meta);
	const [ minzoom, setMinZoom ] = useState(1);
	const [ maxzoom, setMaxZoom ] = useState(100);
	const [ rotation, setRotation ] = useState(0);
	const [ uploaded_image, setUploadedImage ] = useState(false);
	const [ validate_elements, setValidateElements ] = useState({});
	const [ zoom, setZoom ] = useState(100);
	const [ lock, setLock ] = useState(false);
	const [ finished, setFinished ] = useState(false);
	const [ submitted, setSubmitted ] = useState(false);
	//const [ zoomed, setZoomed ] = useState(false);
	//const [ image, setImage ] = useState(null);

	const validateElements = () => {
		let stickers = 0;
		const v = {
			marks:true,
			stickers:true,
		};

		for(let i in meta.stickers) {
			stickers += meta.stickers[i];
		}

		if(stage.photo_editor_num_stickers_min != undefined && stickers < stage.photo_editor_num_stickers_min) {
			v.stickers = 'min';
		}

		if(stage.photo_editor_num_stickers_max != undefined && stickers >= stage.photo_editor_num_stickers_max) {
			v.stickers = 'max';
		}

		setValidateElements(v);
	};

	useEffect(() => {
		validateElements();
	}, []);

	if(promo === false || stage === false) return null;
	//if(has_stickers === false && has_frames === false) return null;

	const toggleItemSelected = (item) => {
		// if we don't have any element selected, we select the main_image:
		if(item == undefined) {
			item = cnv.current.getObjectByID('main_image');
			return;
		}

		if(item.type == "main_image") {
			setZoom(Math.ceil(item.scaleX * 100));
			setLock(item.lockScalingX);
			if(!is_mobile) setDisplay('photo');
		}

		if(item.type == "sticker") {
			setDisplay('stickers');
		}
	};

	const zoomPhoto = (k, v) => {
		const v_int = parseInt(v);
		cnv.current.scaleSelectedItem(v_int);
		setZoom(v_int);
	};

	const rotatePhoto = (k, v) => {
		const v_int = parseInt(v);
		cnv.current.rotateSelectedItem(v_int);
		setRotation(v_int);
	};

	const deleteSelected = () => {
		setDisplayConfirmDelete(true);
	};

	const confirmDeleteSelected = () => {
		const reset = () => {
			cnv.current = undefined;

			setExportedImage(false);
			setDisplayConfirmDelete(false);
			setDisplayEditor(false);
			setUploadedImage(false);
			setMeta(default_meta);
			restoreScrollEvents();
		}
		if (!cnv.current) reset();
		cnv.current.resetCanvas(reset);
	};

	const shittySafariHack = () => {
		if(is_builder) return;

		const canvas_h = document.querySelector('.canvas_container').clientHeight;
		document.querySelector('.gui_canvas_photo_editor .canvas_container').style.width = `${canvas_h}px`;
		document.querySelector('.gui_canvas_photo_editor .gui_canvas_cont').style.width = `${canvas_h}px`;
		document.querySelector('.gui_canvas_photo_editor .gui_canvas_cont').style.height = `${canvas_h}px`;
	};

	const addImage = (k, v) => {
		preventScrollEvents();
		setDisplayEditor(true);

		setTimeout(() => {
			if(k == "image") cnv.current.addB64(v, 'main_image', (item) => {
				validateElements();
				setUploadedImage(true);

				const minzoom = Math.ceil(item.scaleX * 100);
				setMinZoom(minzoom);
				setMaxZoom(minzoom + 100);

				addMandatory();
			});
		}, 100);
	};

	const changeDisplayView = (view) => {
		cnv.current.deselectElements();

		setDisplay(view);
		setDisplayMobileOptions(true);
	};

	const unselectCanvasElement = (e) => {
		if(e.target.nodeName != 'CANVAS' && e.target.nodeName != 'A') {
			cnv.current.deselectElements();
		}
	};

	const submit = (cb) => {
		cb();

		if(props.onFinish !== undefined) props.onFinish(finished, (res) => {
			setSubmitted(res);
		});
	}

	const finish = () => {
		const exported = cnv.current.exportToJpeg();
		//if(props.onFinish !== undefined) props.onFinish(exported.b64, meta, exported.file);

		uploadImage(getRemotedbUrl(`/stages/upload/${stage.id}`), exported.file, (result) => {
			setFinished({
				quiz_entry:{
					media:[{
						name:result.filename,
						type:'image',
					}],
					meta
				}
			});

			setExportedImage(result.full_url);
		});

		restoreScrollEvents();
		setDisplayEditor(false);
		setExportedImage(exported.b64);
	};

	const foldUpMenu = () => {
		setDisplayMobileOptions(false);
	};

	const addElement = (type, data) => {
		const _meta = { ...meta };

		if(type == 'sticker') {
			cnv.current.addImage('sticker', data);
			_meta.stickers[data.image] = (_meta.stickers[data.image] == undefined) ? 1 : _meta.stickers[data.image] + 1;
		}

		if(type == 'overlay') {
			cnv.current.addOverlayImage('overlay', data);
			_meta.marks = data.image;
		}

		setMeta(_meta);
		validateElements();
		foldUpMenu();
	};

	const onRemove = (object) => {
		if(object.id === 'sticker') {
			const _meta = { ...meta };
			_meta.stickers[object.text]--;
			if(_meta.stickers[object.text] == 0) delete _meta.stickers[object.text];
			setMeta(_meta);
		}

		validateElements();
	};

	const removeOverlay = () => {
		cnv.current.removeOverlayImage();

		setMeta({ ...meta, marks:'' });
		foldUpMenu();
	};

	const addMandatory = () => {
		setTimeout(() => {
			if(full_photo_editor_custom !== false) {
				cnv.current.addBackgroundImage(stage.full_photo_editor_custom);
			}

			setUploadedImage(true);

			let photo_editor_mandatory_frame = stage?.photo_editor_mandatory_frame ?? false;
			if(stage.promotion_type_id == spt.SCENES.id) photo_editor_mandatory_frame = true;

			// if frame is not mandatory or we have an instructions image uploaded, we return
			if(photo_editor_mandatory_frame === false || full_photo_editor_custom !== false) return;
			
			// otherwise, we select the first frame in the list and we display it
			const mark = stage.photo_editor_images.marks[0];
			setTimeout(() => {
				addElement('overlay', { url:mark.full, name:mark.name, image:mark.image });
			}, 200);
		}, 0);
	};

	const preventScrollEvents = () => {
		// this was the b**ch that broke the code when scrolling down (on mobile) when in register form. Remember this!
		window.scrollTo(0, 0);

		document.querySelector('body').style.overflow = 'hidden';
	};

	const restoreScrollEvents = () => {
		document.querySelector('body').style.overflow = 'visible';
	};

	const open = () => {
		preventScrollEvents();

		setTimeout(() => {
			setDisplayEditor(true);
			addMandatory();

			if(full_photo_editor_custom === false) return;
		}, 0);
	};

	useEffect(() => {
		if(stage.promotion_type_id != spt.SCENES.id) return;

		open();
	}, []);

	useEffect(() => {
		if((window?.browser ?? 'chrome') != 'safari') return;

		if(!display_editor) {
			window.removeEventListener('resize', shittySafariHack);
			return;
		}

		shittySafariHack();

		window.addEventListener('resize', shittySafariHack);

		return () => {
			window.removeEventListener('resize', shittySafariHack);
		}
	}, [ display_editor ])

	const cancelUpload = () => {
		restoreScrollEvents();
		setUploadedImage(false);
		setDisplayEditor(false);
	};

	const toggleItemLocked = () => {
		cnv.current.toggleLock('main_image');
		setLock(!lock);
	};

	const img_proportion = stage?.photo_editor_proportion ?? '1x1';	// w:h

	const ressolution_factor = .5;

	const preview_styles = { maxWidth:'100%' };
	let width = false;
	let height = false;
	const img_attrs = {};
	switch(img_proportion) {
		case '120x63':
			// POST
			width = 1200 * ressolution_factor;
			height = 630 * ressolution_factor;
			preview_styles.width = '300px';
			img_attrs.width = '100%';
			break;

		case '375x667':
			// STORY
			width = 1127 * ressolution_factor;
			height = 2008 * ressolution_factor;
			preview_styles.height = '300px';
			img_attrs.height = '100%';
			break;

		case '1x1':
		default:
			// SQUARE
			width = 800 * ressolution_factor * 2;
			height = 800 * ressolution_factor * 2;
			preview_styles.width = '300px';
			preview_styles.height = '300px';
			img_attrs.width = '100%';
			break;
	}

	const button_label = (isNotEmpty(stage, 'photo_editor_label')) ? stage.photo_editor_label : ((stage.promotion_type_id != spt.SCENES.id) ? "Upload and edit image" : "Click to start");

	const has_custom_img = (full_photo_editor_custom === false && stage.promotion_type_id != spt.SCENES.id) ? true : false;
	const min_stickers = stage?.photo_editor_num_stickers_min ?? 0;
	const max_stickers = stage?.photo_editor_num_stickers_max ?? false;

	const display_page = props?.display_page ?? false;

	const label_photo = stage?.photo_editor_label_photo ?? "Photo";
	const label_stickers = stage?.photo_editor_label_stickers ?? i18n("Stickers");
	const label_marks = stage?.photo_editor_label_frames ?? ((stage.promotion_type_id == spt.SCENES.id) ? "Scenes" : "Frames");
	const scaling_mode = stage?.photo_editor_scaling_mode ?? '2';

	let min_stickers_msg = '';
	if(is_mobile && has_stickers && min_stickers > 0 && validate_elements.stickers !== true) {
		if(min_stickers != max_stickers) {
			min_stickers_msg = ` (${i18n("Min.")} ${min_stickers})`;
		} else {
			min_stickers_msg = ` (${i18n("Need")} ${min_stickers})`;
		}
	}

	const automatic_display = false;

	const media_label = (isNotEmpty(promo, 'need_photo')) ? promo.need_photo : i18n("Upload a picture");

	const use_fabric = true;
	const Component = (use_fabric) ? SimpleFabric : PhotoFunCanvas;

	if(display_page == 'public_share') {
		return (
			<PhotoFunPublicShare
				stage={stage}
				promotion={promo}
				locale={props.locale}
				user={(!is_builder) ? 'test' : {}}
				is_builder={is_builder}
				intl={props.intl}
			/>
		)
	}

	if(submitted !== false || display_page == 'success') {
		const defaut_text_placeholder = `<h1>${i18n("This is your design!")}</h1><p>${i18n("Share it now with your friends so they can create their own and participate in the contest too.")}</p>`;

		if(submitted?.loyalty) {
			window?.menu_ref?.getWrappedInstance().setTokens({[submitted?.loyalty.token_id]:submitted?.loyalty.total});
		}

		const final_message = {
			text:(isNotEmpty(stage, 'game_feedback_ok')) ? stage.game_feedback_ok : defaut_text_placeholder,
			color:stage?.game_color_ok ?? 'rgba(120, 195, 0, 1)',
		};

		const continueGame = (e) => {
			if(submitted.url != undefined) {
				window.location.href = submitted.url;
				return;
			}
		};

		const restartGame = () => {
			props.canPlayAgain(() => {
				setFinished(false);
				setSubmitted(false);
				setUploadedImage(false);
				setExportedImage(props?.exported_image ?? false);
			});
		};

		const in_public_galleries = submitted?.galleries ?? [];
		const has_public_gallery = (in_public_galleries.length > 0) ? true : false;
		let link_to_public_gallery = null;
		if(has_public_gallery){
			const gallery_url_base = (!is_builder) ? `https://${window.domain_url}` : window.public_url;
			link_to_public_gallery = `${gallery_url_base}/${in_public_galleries[0].url}`;
		}

		let display_share = ((promo.config?.share?.status ?? '1') == '1') ?? true;
		if(!has_public_gallery) display_share = false;

		return (
			<PromoColsWrapper
				//main_image={stage?.main_image && stage.full_main_image}
				//main_video={stage.main_video}
				main_description={(isNotEmpty(stage, 'game_feedback_ok')) ? stage.game_feedback_ok : defaut_text_placeholder}
				extra_classes='photofun_success'
			>
				<EarnedPointsMessage
					loyalty={submitted?.loyalty}
					promotion={promo}
				/>

				<div class="uploaded_image" style={preview_styles}>
					{(!is_builder) ? (
						<img src={`${exported_image}`} {...img_attrs} />
					) : (
						<span style={{
							backgroundColor:'rgba(var(--text-color-rgb), 0.05)',
							display:'inline-block',
							...preview_styles,
						}}>
							<img src={`/img/blank_${img_proportion}.png`} {...img_attrs} />
						</span>
					)}
				</div>

				{(display_share) && (
					<ShareThis
						stage={stage}
						locale={props.locale}
						promotion={promo}
						result={submitted}
						is_builder={is_builder}
						intl={props.intl}
					/>
				)}

				<div class="photofun_actions">
					<ActionLink
						_href={exported_image}
						//icon='knk-download'
						_target='_blank'
						download='my-image.jpg'
					>Download</ActionLink>

					{(link_to_public_gallery) && (
						<ActionLink
							_href={link_to_public_gallery}
							//icon='knk-visible'
						>View all participants</ActionLink>
					)}

					<NextActionButton
						custom_color={final_message.color}
						multiparticipation={promo.multi_participation}
						login_type={promo.login_type}
						status='ok'
						allow_retry={submitted.retries > 1}
						retries={submitted.retries}
						prize={submitted.prize}
						next_participation={submitted.next_participation}
						continueGame={() => continueGame()}
						restartGame={() => restartGame()}
						intl={props.intl}
						is_builder={is_builder}
						promotion_id={promo.id}
						type='link'
					/>
				</div>
			</PromoColsWrapper>
		)
	}

	let default_description_text = `<h1>${i18n("Upload your image")}</h1><p>${i18n("You will be able to customize it later.")}</p>`;
	if(stage.promotion_type_id == spt.SCENES.id) default_description_text = `<h1>${i18n("Preview and submit")}</h1>`;

	const direct_description = (isNotEmpty(stage, 'main_description')) ? stage.main_description : default_description_text;

	const is_enabled = (has_stickers === false && has_frames === false) ? false : true;

	return (
		<PromoColsWrapper
			main_image={stage?.main_image && stage.full_main_image}
			main_video={stage.main_video}
			main_description={direct_description}
		>
			{(!is_enabled) && (
				<AlertMessage
					icon={false}
					title={i18n("First add some stickers / frames")}
					title_component='strong'
					custom_styles={{
						'--text-color':'255, 255, 255, 1',
						'--text-default-align':'center',
					}}
					title_styles={{
						display:'inline-block'
					}}
				/>
			)}

			{(is_enabled && automatic_display && !exported_image) && (
				<section class="gui_canvas_photo_editor gui_is_not_padded">
					<div class="gui_pos_rel" style={{ width:'100vw', height:'100dvh', padding:'5vw' }}>
						<ImageUploader
							name="image"
							_class={{ first:true, other:'gui_not_bordered' }}
							//image={image}
							change={(!is_builder) ? addImage : null}
							disabled={(is_builder) ? true : false}
							_size={{ width, height }}
							_thumb_size={{ width:'calc(90vw - 6px)', height:'calc(100dvh - 10vw - 6px)' }}
							small_input={false}
							enable_cancel={false}
						/>
					</div>
				</section>
			)}

			{(is_enabled && !exported_image && stage.promotion_type_id != spt.SCENES.id && full_photo_editor_custom === false) && (
				<ImageUploader
					name="image"
					//image={image}
					change={(!is_builder) ? addImage : null}
					_size={{ width, height }}
					_class={{
						other:`photofun_uploader${(is_builder) ? ' gui_remove_pointer_events' : ''}`,
					}}
					label_props={{
						autotranslate:false,
					}}
					small_input={false}
					display_custom_dragging_instructions={button_label}
				/>
			)}

			{(is_enabled && !exported_image && stage.promotion_type_id == spt.SCENES.id) && (
				<ButtonCollection
					_class={{ other:'flex_button gui_big_button' }}
				>
					<ActionButton
						_fullclass=""
						color="transparent"
						onClick={open}
						autotranslate={false}
					>{button_label}</ActionButton>
				</ButtonCollection>
			)}

			{(exported_image) && (
				<div class="input field required field_UserMedia" style={{ alignItems:'center' }}>
					<div class="photofun_result">
						<div class="uploaded_image" style={preview_styles}>
							<img src={`${exported_image}`} {...img_attrs} />
						</div>

						<div class="gui_combined_actions_col">
							<ButtonCollection
								_class={{ other:'flex_button gui_big_button gui_not_in_top gui_not_in_bottom_small' }}
							>
								<SaveButton
									_fullclass="in_top"
									disabled={(!finished) ? true : false}
									onClick={(e, cb) => submit(cb)}
								>{(finished) ? i18n("Submit") : `${i18n("Processing")}...`}</SaveButton>
							</ButtonCollection>

							<ActionLink
								onClick={deleteSelected}
							>Start over</ActionLink>
						</div>
					</div>
				</div>
			)}

			{(display_editor) && (
				<section class={'gui_canvas_photo_editor' + ((is_builder) ? ' in_builder' : '')} onClick={(e) => unselectCanvasElement(e)}>
					<div class={'canvas_container' + ((display_mobile_options) ? ' with_options_visible' : '')}>
						<div class={'gui_canvas_cont' + ((!uploaded_image) ? ' gui_pos_rel' : '')}>
							<div>
								<Component
									ref={(node) => { cnv.current = node; }}
									fabricSelected={toggleItemSelected}
									fabricUnselected={toggleItemSelected}
									scalling_mode={scaling_mode}
									margins={false}
									onRemove={onRemove}
									_width={width}
									_height={height}
								/>
							</div>

							{(!uploaded_image) && (
								<ImageUploader
									only_element={true}
									element="true"
									_class={{first:true, other:'fit_to_parent'}}
									name="image"
									//image={image}
									change={addImage}
									_size={{width:width, height:height}}
									_thumb_size={{width:'100%', height:'100%'}}
									enable_cancel={((!is_builder) ? cancelUpload : null)}
								/>
							)}
						</div>
					</div>

					{(uploaded_image) && (
						<div class="photo_editor_controls solid_buttons">
							<nav class="photo_editor_actions_nav">
								{(has_custom_img && !is_mobile) && (
									<ActionButton
										color='black'
										icon='knk-picture'
										_class={{ other:'whiteIcon original_color_item photo_tab'+((display == 'photo') ? ' selected' : '') }}
										onClick={() => changeDisplayView('photo')}
									>{label_photo}</ActionButton>
								)}

								{(has_custom_img && is_mobile) && (
									<ActionButton
										color={(lock) ? 'black' : 'gray'}
										icon={(lock) ? 'knk-zipped' : 'knk-unzipped'}
										_class={{ other:'whiteIcon original_color_item'+((display == 'photo') ? ' selected' : '') }}
										onClick={toggleItemLocked}
									>{label_photo}</ActionButton>
								)}

								{(has_frames) && (
									<ActionButton
										color='black'
										icon='knk-frame'
										_class={{ other:'whiteIcon original_color_item frames_tab'+((display == 'frames') ? ' selected' : '') }}
										onClick={() => changeDisplayView('frames')}
									>{label_marks}</ActionButton>
								)}

								{(has_stickers) && (
									<ActionButton
										color='black'
										icon='knk-sticker'
										_class={{ other:'whiteIcon original_color_item stickers_tab'+((display == 'stickers') ? ' selected' : '') }}
										onClick={() => changeDisplayView('stickers')}
										autotranslate={false}
									>{label_stickers + min_stickers_msg}</ActionButton>
								)}
							</nav>

							<article class={'selectors'+((display_mobile_options) ? ' visible' : '')}>
								{(display == 'photo') && (
									<div class="main_image_settings">
										<Range
											_class={{ first:true }}
											type="zoom"
											min={minzoom}
											max={maxzoom}
											change={zoomPhoto}
											name="img_zoom"
											value={zoom}
											default={100}
										/>

										<Range
											_class={{ first:true }}
											type="rotate"
											min={-180}
											max={180}
											change={rotatePhoto}
											name="img_rotation"
											value={rotation}
											default={0}
										/>

										<ActionButton
											color='transparent'
											icon={(lock) ? 'knk-zipped' : 'knk-unzipped'}
											_class={{other:'lock_button'}}
											onClick={toggleItemLocked}
										>{(lock) ? "Unlock" : "Lock"}</ActionButton>
									</div>
								)}

								{(has_stickers && display == 'stickers') && (
									<StickerSelector
										items={stickers}
										type='sticker'
										addItem={addElement}
										is_mobile={is_mobile}
										meta={meta.stickers}
										validation={validate_elements[display]}
										stage={stage}
										promo={promo}
										intl={props.intl}
									/>
								)}

								{(has_frames && display == 'frames') && (
									<ItemSelector
										items={marks}
										type='overlay'
										addItem={addElement}
										removeItem={removeOverlay}
										is_mobile={is_mobile}
										meta={meta.marks}
										stage={stage}
										promo={promo}
										intl={props.intl}
									/>
								)}

								<aside class="fold_up_menu original_color_item">
									<ActionButton
										color="transparent"
										_class={{ other:'blackColor' }}
										onClick={foldUpMenu}
									>Close</ActionButton>
								</aside>
							</article>

							<ButtonCollection
								id="photo_editor_header"
								type='nav'
								_class={{ other:'photo_editor_actions_nav flex_button' }}
							>
								<ActionButton
									color='red'
									_class={{ other:'original_color_item cancel_button' }}
									onClick={deleteSelected}
								>Cancel</ActionButton>

								<ActionButton
									disabled={validate_elements.stickers == 'min'}
									color='green'
									_class={{ other:'original_color_item accept_button' }}
									onClick={finish}
									autotranslate={false}
								>{i18n("Accept")}{((min_stickers > 0 && min_stickers < max_stickers) ? ` (${i18n("Min.")} ${min_stickers})` : '')}</ActionButton>
							</ButtonCollection>
						</div>
					)}

					{(is_builder) && (
						<CloseButton
							_class={{ other:'blackIcon' }}
							onClick={confirmDeleteSelected}
						/>
					)}
				</section>
			)}

			{(display_confirm_delete) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Confirmation of cancellation")}</h1>`}
					pm={promo}
					backButton={() => setDisplayConfirmDelete(false)}
					is_builder={is_builder}
				>
					<LeveledHTMLText>
						<p>{i18n("Are you sure you want to cancel this?")}</p>
						<p>{i18n("You will lose any changes that may be applied.")}</p>
					</LeveledHTMLText>

					<div class="gui_combined_actions_col gui_not_in_top_big">
						<ButtonCollection _class={{ other:'flex_button' }}>
							<ActionButton
								_fullclass=""
								onClick={confirmDeleteSelected}
							>Yes, remove</ActionButton>
						</ButtonCollection>

						<ActionLink
							onClick={() => setDisplayConfirmDelete(false)}
						>No, keep working</ActionLink>
					</div>
				</FloatingLayerPublic>
			)}
		</PromoColsWrapper>
	)
};

export default injectIntl(PhotoFunStage);